/**
 * Icon set for the marketing app-shell mockup.
 *
 * Deliberately a local set, not a shared primitive: these exist only to make the
 * mockup read as the real product. They are decorative — every one is
 * aria-hidden, and the surrounding markup carries the accessible name.
 *
 * All icons are 24x24 stroke-only on currentColor so a single `size` prop and
 * the parent's `color` drive them.
 */

interface IconProps {
  size?: number;
  className?: string;
}

/**
 * Marks an icon whose GLYPH points somewhere, so it flips under RTL.
 *
 * Baked into the icon rather than applied at each call site, which is the same
 * decision the app made when it tagged these `pi--mirror`: a directional icon
 * that has to be remembered at every usage is one usage away from pointing
 * backwards, and it passes review in whichever locale the reviewer reads.
 *
 * NOT every arrow qualifies. Play is a transport control and transport does not
 * mirror — playback runs left to right in every locale. The rule is whether the
 * glyph encodes READING direction or something else.
 */
const MIRROR = 'pn-mirror';

function svg(path: React.ReactNode, { size = 18, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {path}
    </svg>
  );
}

/* ── Top bar ─────────────────────────────────────────────────────────────── */

export const IconMenu = (p: IconProps) =>
  svg(
    <>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </>,
    p
  );

export const IconSearch = (p: IconProps) =>
  svg(
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.6-3.6" />
    </>,
    p
  );

export const IconPlus = (p: IconProps) =>
  svg(
    <>
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </>,
    p
  );

export const IconChevronDown = (p: IconProps) => svg(<path d="m6 9 6 6 6-6" />, p);

/* Tab 5's "Available for processing" line. A ringed tick rather than the bare
   IconCheck, because the app's own status line is a filled affirmative marker
   and a loose tick beside text reads as a checkbox someone ticked. */
export const IconCheckCircle = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12 2.5 2.5 4.5-5" />
    </>,
    p
  );

/* ── Sidebar ─────────────────────────────────────────────────────────────── */

export const IconHome = (p: IconProps) =>
  svg(
    <>
      <path d="m3 10.5 9-7 9 7" />
      <path d="M5.5 9.5V20h13V9.5" />
    </>,
    p
  );

/* MIRRORED (owner, 2026-09-08). The three stubs at x=4 are a bullet gutter and
   the long rules beside them are the text — so the glyph is a miniature of a
   list, and a list's bullets sit on the side you start reading from. Left in
   LTR, right in RTL. That is the reading-direction test at the top of this file,
   not an arrow test: nothing here points anywhere, but the layout is still
   directional, which is why it was easy to miss when the rest were audited. */
export const IconMeetings = (p: IconProps) =>
  svg(
    <>
      <path d="M4 6h1" />
      <path d="M9 6h11" />
      <path d="M4 12h1" />
      <path d="M9 12h11" />
      <path d="M4 18h1" />
      <path d="M9 18h11" />
    </>,
    { ...p, className: [p.className, MIRROR].filter(Boolean).join(' ') }
  );

export const IconCalendar = (p: IconProps) =>
  svg(
    <>
      <rect x="3.5" y="5" width="17" height="15" rx="2.5" />
      <path d="M3.5 10h17" />
      <path d="M8 3.5v3" />
      <path d="M16 3.5v3" />
    </>,
    p
  );

export const IconActions = (p: IconProps) =>
  svg(
    <>
      <rect x="5" y="4.5" width="14" height="16" rx="2.5" />
      <path d="M9 3.5h6v3H9z" />
      <path d="m9.5 12 1.8 1.8 3.4-3.6" />
    </>,
    p
  );

export const IconInsights = (p: IconProps) =>
  svg(
    <>
      <path d="M4 19h16" />
      <path d="m5 15 4.5-5 3.5 3.2L19 6.5" />
    </>,
    p
  );

export const IconTemplates = (p: IconProps) =>
  svg(
    <>
      <path d="M12 6.5S10 4.5 4 4.5v13c6 0 8 2 8 2s2-2 8-2v-13c-6 0-8 2-8 2Z" />
      <path d="M12 6.5v15" />
    </>,
    p
  );

/* The escape arrow leaves toward the top-END corner and the panel it leaves sits
   at the bottom-START one, so both halves have to move together under RTL. */
export const IconExternal = (p: IconProps) =>
  svg(
    <>
      <path d="M13.5 5H19v5.5" />
      <path d="M19 5l-7 7" />
      <path d="M18 14.5V18a1.5 1.5 0 0 1-1.5 1.5h-10A1.5 1.5 0 0 1 5 18V8a1.5 1.5 0 0 1 1.5-1.5H10" />
    </>,
    { ...p, className: [p.className, MIRROR].filter(Boolean).join(' ') }
  );

/* ── Content nav ─────────────────────────────────────────────────────────── */

export const IconMinutes = (p: IconProps) =>
  svg(
    <>
      <rect x="5" y="4" width="14" height="17" rx="2" />
      <path d="M8.5 9h7" />
      <path d="M8.5 13h7" />
      <path d="M8.5 17h4" />
    </>,
    p
  );

export const IconTranscript = (p: IconProps) =>
  svg(
    <>
      <rect x="4.5" y="4.5" width="15" height="15" rx="2.5" />
      <path d="M8 9.5h8" />
      <path d="M8 13.5h5" />
    </>,
    p
  );

export const IconAudio = (p: IconProps) =>
  svg(
    <>
      <rect x="9.5" y="3.5" width="5" height="10.5" rx="2.5" />
      <path d="M6 12a6 6 0 0 0 12 0" />
      <path d="M12 18v2.5" />
    </>,
    p
  );

/* The recorder's own glyph, matching the app's Icon.Mic exactly (MoMP
   Icons.tsx:54) rather than IconAudio above — the app draws the mic with its
   stand in the recorder rail and the capsule-only variant in the section nav,
   and the two read as different things. */
export const IconMic = (p: IconProps) =>
  svg(
    <>
      <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="8" y1="23" x2="16" y2="23" />
    </>,
    p
  );

export const IconCheck = (p: IconProps) => svg(<path d="m5 12.5 4.5 4.5L19 7" />, p);

export const IconShare = (p: IconProps) =>
  svg(
    <>
      <path d="M12 15.5V4" />
      <path d="m8 8 4-4 4 4" />
      <path d="M5 14v5.5a1.5 1.5 0 0 0 1.5 1.5h11a1.5 1.5 0 0 0 1.5-1.5V14" />
    </>,
    p
  );

/* ── Detail header actions ───────────────────────────────────────────────── */

export const IconProcess = (p: IconProps) =>
  svg(
    <>
      <path d="M20 12a8 8 0 1 1-2.6-5.9" />
      <path d="M20 4.5V10h-5.5" />
    </>,
    p
  );

export const IconExport = (p: IconProps) =>
  svg(
    <>
      <path d="M12 15V4" />
      <path d="m8 8 4-4 4 4" />
      <path d="M5 14v5.5a1.5 1.5 0 0 0 1.5 1.5h11a1.5 1.5 0 0 0 1.5-1.5V14" />
    </>,
    p
  );

export const IconCollapse = (p: IconProps) =>
  svg(
    <>
      <path d="M9.5 14.5 4.5 19.5" />
      <path d="M9.5 19.5v-5h-5" />
      <path d="M14.5 9.5 19.5 4.5" />
      <path d="M14.5 4.5v5h5" />
    </>,
    p
  );

export const IconClose = (p: IconProps) =>
  svg(
    <>
      <path d="m6 6 12 12" />
      <path d="M18 6 6 18" />
    </>,
    p
  );

export const IconDoc = (p: IconProps) =>
  svg(
    <>
      <path d="M13.5 3.5H7A1.5 1.5 0 0 0 5.5 5v14A1.5 1.5 0 0 0 7 20.5h10a1.5 1.5 0 0 0 1.5-1.5V8.5Z" />
      <path d="M13.5 3.5v5h5" />
    </>,
    p
  );

/* ── Recorder transport ───────────────────────────────────────────────────
   TRANSCRIBED FROM THE APP, path for path — MoMP's components/Icons.tsx
   Volume / Pause / Square / Play / Send / Download / Trash, which is the set
   RecorderBody.tsx renders. IconMic above is already the recorder's mic.

   The first pass drew look-alikes from memory and the owner spotted them side
   by side on 2026-09-06. Two were not just approximations but the wrong KIND of
   glyph: stop and play are FILLED in the app and were outlined here, which at
   28px is the difference between a stop button and an empty box. */

export const IconVolume = (p: IconProps) =>
  svg(
    <>
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
      <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
    </>,
    p
  );

export const IconPause = (p: IconProps) =>
  svg(
    <>
      <rect x="6" y="4" width="4" height="16" rx="1" />
      <rect x="14" y="4" width="4" height="16" rx="1" />
    </>,
    p
  );

/* Icon.Square — filled, stroke removed. */
export const IconStop = (p: IconProps) =>
  svg(
    <rect x="4" y="4" width="16" height="16" rx="2" fill="currentColor" stroke="none" />,
    p
  );

export const IconPlay = (p: IconProps) =>
  svg(<polygon points="5 3 19 12 5 21 5 3" fill="currentColor" stroke="none" />, p);

/* The app tags Send `pi--mirror` so it flips under RTL, and this now does the
   same — the TODO this comment used to carry was the AR pass, which is done. */
export const IconSend = (p: IconProps) =>
  svg(
    <>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </>,
    { ...p, className: [p.className, MIRROR].filter(Boolean).join(' ') }
  );

export const IconDownload = (p: IconProps) =>
  svg(
    <>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </>,
    p
  );

/* ── Floating-window chrome ───────────────────────────────────────────────
   The pop-out's title bar. "Back to tab" and close are the two controls a
   Document Picture-in-Picture window actually has; the pin is ours — it is the
   established keep-on-top affordance (Zoom, Teams, VLC) and it is what tells a
   visitor in one glyph that this panel outlives the page behind it. */

/* Pushpin, upright, head at the top and point at the bottom. The first attempt
   drew a pin lying on its side and it read as a paper plane at 13px — a keep-on-
   top affordance is worth nothing if it is not recognised instantly. */
export const IconPin = (p: IconProps) =>
  svg(
    <>
      <line x1="12" y1="17" x2="12" y2="22" />
      <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
    </>,
    p
  );

/* Back to tab — the frame-in-frame the browser itself uses for picture-in-
   picture. Deliberately NOT an arrow: the first version drew one pointing
   down-right, which is "pop OUT", and reversing it produced a shape that was
   illegible at this size. A small pane nested in a large one needs no arrow to
   say which is going back into which. */
export const IconPopIn = (p: IconProps) =>
  svg(
    <>
      <path d="M21 10.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3.5" />
      <rect x="11.5" y="12" width="9.5" height="7.5" rx="1.5" />
    </>,
    p
  );

export const IconTrash = (p: IconProps) =>
  svg(
    <>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </>,
    p
  );

/* ── Action Items ────────────────────────────────────────────────────────── */

/* The page-head mark. Distinct from IconActions (the SIDEBAR entry, a clipboard):
   the app uses a check-in-square for the page title and the clipboard for the
   nav row, and having both on screen at once is exactly when the difference
   shows. */
export const IconCheckSquare = (p: IconProps) =>
  svg(
    <>
      <path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h9.5" />
      <path d="m8.5 11.5 3 3 8-8.5" />
    </>,
    p
  );

/* The KPI cards' corner cue and the Filter button's leading mark are the SAME
   funnel in the app — the cards are filter buttons, and the shared glyph is what
   says so. One export, two consumers. */
export const IconFunnel = (p: IconProps) =>
  svg(<path d="M3 5h18l-7 9v6l-4-2v-4z" />, p);

/* Mobile row expander. Tagged pi--mirror because it points along the reading
   direction: under RTL an unmirrored chevron points back out of the row it is
   supposed to open. */
export const IconChevronRight = (p: IconProps) =>
  svg(<path d="m9 6 6 6-6 6" />, { ...p, className: [p.className, 'pi--mirror'].filter(Boolean).join(' ') });

export const IconMoreVertical = (p: IconProps) =>
  svg(
    <>
      <circle cx="12" cy="5" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
      <circle cx="12" cy="19" r="1.2" fill="currentColor" stroke="none" />
    </>,
    p
  );

/* The hover cue on an editable cell. */
export const IconPencil = (p: IconProps) =>
  svg(
    <>
      <path d="M16.5 4.5a2.1 2.1 0 0 1 3 3L8 19l-4 1 1-4z" />
      <path d="m14.5 6.5 3 3" />
    </>,
    p
  );
